import { useEffect, useRef, useState } from 'react';
import type { DemoScript, Fx, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, TYPE, alpha, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';

/**
 * QUARTERMASTER · POUR TO THE LINE — hold to pour, let go on the line.
 *
 * The toast of the evening, made into a steady hand. Holding anywhere on the board tips the bottle
 * and pours; letting go stops it. Stopping short is fine (hold again to top it up); going past the
 * band round the line spills the glass, which is the mistake, and it is poured again from empty.
 * Round 1 pours slowly with a wide band; round 2 a little quicker, the band a little narrower and
 * the pour speeding up the longer you hold; round 3 quicker again, and the head keeps rising a
 * little after you let go — so a good pour stops just before the line.
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`PourView`), a
 * pure `View`, the `Board` that plays it on the game clock, and the `demo` the how-to card plays.
 */

export interface Pour {
  /** Fill per second while held, as a share of the glass. */
  speed: number;
  /** How much faster it runs the longer you hold: ×(1 + accel × seconds held). */
  accel: number;
  /** Half the band round the line that counts as on it. */
  band: number;
  /** How much the head rises after letting go. */
  head: number;
  /** Where the line sits, ± a little each round. */
  line: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const POURS: readonly Pour[] = easier([
  { speed: 0.2, accel: 0, band: 0.055, head: 0, line: 0.6 },
  { speed: 0.28, accel: 0.25, band: 0.045, head: 0.02, line: 0.7 },
]);

/** How long the head takes to settle, and the verdict waits for it. */
const SETTLE_MS = 450;
/** A spilt glass shows this long before it is set up again, empty. */
const SPILL_MS = 900;
/** The bottle tips over, or back, in this long. */
const TIP_MS = 250;

/** The glass after `dt` seconds more of pouring, held for `held` seconds so far. */
export function pourOn(fill: number, held: number, dt: number, pour: Pick<Pour, 'speed' | 'accel'>): number {
  return fill + pour.speed * (1 + pour.accel * held) * dt;
}

/** Where the glass stands against the line: past the band spills, in it is right, below it is short. */
export function verdictOf(fill: number, pour: Pick<Pour, 'line' | 'band'>): 'over' | 'on' | 'short' {
  if (fill > pour.line + pour.band) return 'over';
  return fill >= pour.line - pour.band ? 'on' : 'short';
}

/** Everything the glass shows. */
export interface PourView {
  /** The game clock, ms: the bubbles rise by it. */
  t: number;
  /** The drink, as a share of the glass (0 to just over 1). */
  f: number;
  /** The line, and half the band round it, as shares of the glass. */
  l: number;
  b: number;
  /** How far the bottle is tipped over the glass: 0 at rest, 1 pouring. */
  tip: number;
  /** 0 ready, 1 pouring, 2 let go (the head settles), 3 short, 4 spilt, 5 poured right. */
  s: 0 | 1 | 2 | 3 | 4 | 5;
  fx?: Fx | undefined;
}

/** The drink, by era: champagne on Earth, neon in the city, the ration water, the brew, the juice. */
const DRINK: Record<Era, { top: string; body: string }> = {
  stars: { top: '#fff4c8', body: '#e8c35a' },
  neon: { top: '#ffd1f4', body: '#ff4fd8' },
  debris: { top: '#d6f0ff', body: '#7fb8d8' },
  dust: { top: '#ffe6bf', body: '#c9772f' },
  canopy: { top: '#e9ffd0', body: '#8fcf5a' },
};

// The glass, drawn in a 100 × 150 box with the bottle above it (up to −53 at rest): a tall glass
// that narrows a touch to its foot, set in the middle of the board at `S` times that size.
const GLASS_TOP = 18;
const GLASS_BOTTOM = 145;
const GLASS_PATH = 'M24 18 L76 18 L71 138 Q70 146 62 146 L38 146 Q30 146 29 138 Z';
const S = 1.5;
const OX = BOARD.w / 2 - 50 * S;
const OY = 2 + 53 * S;
/** The y of a fill share inside the glass, in the glass's own box. */
const yOf = (f: number) => GLASS_BOTTOM - f * (GLASS_BOTTOM - GLASS_TOP);
/** The same on the board. */
const boardY = (f: number) => OY + yOf(f) * S;
/** The bubbles: across the glass, and how long each takes to rise (s). */
const BUBBLES = [
  [48, 1.6],
  [34, 1.95],
  [62, 2.3],
  [38, 2.65],
] as const;
const smooth = (f: number) => f * f * (3 - 2 * f);

/** The glass, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<PourView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const drink = DRINK[era];
  // A demo glides every number between its keys: the state is read rounded.
  const state = Math.round(view.s);
  const f = Math.max(0, view.f);
  const surface = yOf(Math.min(1.02, f));
  const spilt = state === 4;
  const tip = smooth(clamp(view.tip, 0, 1));
  const lo = view.l - view.b;
  const hi = view.l + view.b;
  const status =
    state === 5
      ? { text: say(lang, '✓ PERFECT', '✓ PERFEKT'), tone: 'ok' as const }
      : state === 4
        ? { text: say(lang, 'SPILT — AGAIN', 'SØLT – PRØV IGJEN'), tone: 'bad' as const }
        : state === 3
          ? { text: say(lang, 'A LITTLE MORE', 'LITT TIL'), tone: 'warn' as const, hint: say(lang, 'HOLD TO TOP UP', 'HOLD FOR Å FYLLE PÅ') }
          : state === 0
            ? { text: say(lang, 'HOLD TO POUR', 'HOLD FOR Å SKJENKE') }
            : { text: say(lang, 'LET GO ON THE LINE', 'SLIPP PÅ STREKEN') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <clipPath id={`${id}glass`}>
          <path d={GLASS_PATH} />
        </clipPath>
        <linearGradient id={`${id}drink`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={drink.top} />
          <stop offset="1" stopColor={drink.body} />
        </linearGradient>
        <linearGradient id={`${id}shine`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" stopOpacity=".16" />
          <stop offset=".35" stopColor="#ffffff" stopOpacity=".04" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g transform={`translate(${OX} ${OY}) scale(${S})`}>
        {/* the bottle: its mouth over the glass, tipped right over while it pours */}
        <g transform={`translate(${68 - 18 * tip} ${-8 + 4 * tip}) rotate(${-164 + 36 * tip})`}>
          <rect x="-2.6" y="0" width="5.2" height="9" rx="1.2" fill={alpha(skin.ink, 0.67)} />
          <path d="M-2.6 9 Q-7 12 -7 17 L-7 44 Q-7 47 -4 47 L4 47 Q7 47 7 44 L7 17 Q7 12 2.6 9 Z" fill={alpha(skin.ink, 0.2)} stroke={skin.ink} strokeWidth="1" />
          <rect x="-5" y="24" width="10" height="11" rx="1.5" fill={alpha(skin.haze, 0.33)} />
        </g>
        {state === 1 && tip > 0.6 && <line x1="50" y1="-4" x2="50" y2={surface} stroke={drink.top} strokeWidth="2.4" strokeLinecap="round" opacity=".9" />}

        {/* the band round the line, and the line */}
        <rect x="20" y={yOf(hi)} width="60" height={yOf(lo) - yOf(hi)} fill={accent} opacity=".26" />
        <path d={`M20 ${yOf(hi)} H80 M20 ${yOf(lo)} H80`} stroke={accent} strokeOpacity=".6" strokeWidth=".8" />
        <line x1="16" y1={yOf(view.l)} x2="84" y2={yOf(view.l)} stroke={INK.hi} strokeWidth="1.2" strokeDasharray="3 2.5" />

        {/* the drink, and its bubbles rising by the clock */}
        <g clipPath={`url(#${id}glass)`}>
          <rect x="0" y={surface} width="100" height={160} fill={`url(#${id}drink)`} opacity={spilt ? 0.35 : 0.92} />
          {f > 0.02 && <ellipse cx="50" cy={surface} rx="30" ry="1.6" fill={drink.top} opacity=".9" />}
          {f > 0.1 &&
            BUBBLES.map(([x, dur], i) => {
              const rise = (view.t / 1000 / dur + i * 0.27) % 1;
              return <circle key={i} cx={x} cy={GLASS_BOTTOM - rise * (GLASS_BOTTOM - surface - 2)} r={0.9 + (i % 2) * 0.4} fill="#ffffff" opacity=".55" />;
            })}
        </g>

        {/* the glass over it */}
        <path d={GLASS_PATH} fill={`url(#${id}shine)`} stroke={INK.text} strokeOpacity=".75" strokeWidth="1.3" />
        <rect x="34" y="146" width="32" height="3" rx="1.5" fill={INK.text} opacity=".5" />

        {/* over the top: it runs down the outside */}
        {spilt && (
          <g>
            <path d="M26 18 Q22 50 25 90" stroke={drink.body} strokeWidth="2" fill="none" opacity=".8" />
            <path d="M74 18 Q79 60 75 110" stroke={drink.body} strokeWidth="2" fill="none" opacity=".8" />
          </g>
        )}
      </g>

      <text x={OX + 88 * S} y={boardY(view.l) + TYPE.hint * 0.36} fontSize={TYPE.hint} fontWeight="700" letterSpacing="1" fontFamily={mono} fill={INK.hi} opacity=".85">
        {say(lang, 'LINE', 'STREK')}
      </text>
    </BoardFrame>
  );
}

type PourState = 'ready' | 'pouring' | 'settling' | 'short' | 'spilt' | 'done';
const STATE_CODE: Record<PourState, PourView['s']> = { ready: 0, pouring: 1, settling: 2, short: 3, spilt: 4, done: 5 };

function Board(props: RoundProps) {
  const { level, lang } = props;
  const [pour] = useState<Pour>(() => {
    const rand = seeded(Date.now() + level * 7919);
    const base = POURS[level]!;
    return { ...base, line: base.line + (rand() - 0.5) * 0.1 };
  });
  const fill = useRef(0);
  const holdingSince = useRef<number | null>(null);
  /** Head still to rise after a release, and when the verdict is due. */
  const rising = useRef(0);
  const verdictAt = useRef<number | null>(null);
  const state = useRef<PourState>('ready');
  const spiltAt = useRef(0);
  const tip = useRef(0);

  const round = useRound(props, (dt, now, won) => {
    // The bottle tips over while held, and back when let go.
    tip.current = clamp(tip.current + ((holdingSince.current !== null ? 1 : -1) * dt * 1000) / TIP_MS, 0, 1);
    if (won) return;
    if (state.current === 'spilt') {
      // A spilt glass is emptied and set up again.
      if (now - spiltAt.current >= SPILL_MS) {
        fill.current = 0;
        state.current = 'ready';
      }
      return;
    }
    if (holdingSince.current !== null) fill.current = pourOn(fill.current, (now - holdingSince.current) / 1000, dt, pour);
    if (rising.current > 0) {
      const step = Math.min(rising.current, (pour.head / (SETTLE_MS / 1000)) * dt * 1.4);
      fill.current += step;
      rising.current -= step;
    }
    if (verdictOf(fill.current, pour) === 'over') {
      // Over the top: the mistake, and the glass is poured again from empty.
      holdingSince.current = null;
      verdictAt.current = null;
      rising.current = 0;
      state.current = 'spilt';
      spiltAt.current = now;
      round.progress(0);
      round.miss();
      return;
    }
    round.progress(Math.min(0.95, fill.current / pour.line));
    if (verdictAt.current !== null && now >= verdictAt.current && holdingSince.current === null) {
      verdictAt.current = null;
      if (verdictOf(fill.current, pour) === 'on') {
        state.current = 'done';
        round.win();
      } else state.current = 'short';
    }
  });

  const press = () => {
    if (!round.live || state.current === 'spilt' || state.current === 'done') return;
    holdingSince.current = round.time();
    verdictAt.current = null;
    state.current = 'pouring';
  };
  const release = () => {
    if (holdingSince.current === null) return;
    holdingSince.current = null;
    rising.current += pour.head;
    verdictAt.current = round.time() + SETTLE_MS;
    state.current = 'settling';
  };
  // Covered mid-pour: the pour stops there, as if let go.
  useEffect(() => {
    if (props.paused) release();
  }, [props.paused]);

  const view: PourView = {
    t: q(round.now),
    f: q(fill.current, 3),
    l: q(pour.line, 3),
    b: pour.band,
    tip: q(tip.current, 2),
    s: STATE_CODE[state.current],
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Hold to pour', 'Hold for å skjenke'), down: press, up: release }}
    />
  );
}

/**
 * The how-to, poured by round 1's own speed: the finger holds, the bottle tips and the drink rises
 * to the line; it lets go inside the band, and the glass is right.
 */
function demoPour(): DemoScript<PourView> {
  const L = POURS[0]!;
  const press = 900;
  const flowing = press + TIP_MS;
  // Let go a little under the line, well inside the band.
  const letGo = flowing + Math.round(((L.line - 0.015) / L.speed) * 1000);
  const settled = letGo + SETTLE_MS;
  return {
    ms: 7_000,
    keys: [
      { at: 0, view: { t: 0, f: 0, l: L.line, b: L.band, tip: 0, s: 0 } },
      { at: press, view: { s: 1 }, ease: 'step' },
      { at: flowing, view: { tip: 1 } },
      { at: letGo, view: { f: q(pourOn(0, 0, (letGo - flowing) / 1000, L), 3) } },
      { at: letGo + 1, view: { s: 2 }, ease: 'step' },
      { at: letGo + TIP_MS, view: { tip: 0 } },
      { at: settled, view: { s: 5, fx: [1, 0] }, ease: 'step' },
      { at: settled + 500, view: { fx: [0, 0] } },
    ],
    finger: [
      { at: 0, x: 266, y: 292, hide: true },
      { at: 400, x: 266, y: 292, hide: false },
      { at: 850, x: 262, y: 250 },
      { at: press, x: 262, y: 250, down: true },
      { at: letGo, x: 262, y: 250, down: false },
      { at: 5_600, x: 266, y: 292, hide: true },
    ],
    steps: [
      { at: 0, en: 'HOLD TO POUR', no: 'HOLD FOR Å SKJENKE' },
      { at: 2_400, en: 'LET GO ON THE LINE', no: 'SLIPP PÅ STREKEN' },
      { at: settled, en: 'PAST THE LINE SPILLS', no: 'OVER STREKEN SØLER' },
    ],
  };
}

export const game = thirtyGame<PourView>({
  Board,
  View,
  demo: demoPour(),
  words: {
    en: {
      instruction: 'Hold to pour, let go on the line. Past the line spills. Three glasses, each quicker.',
      howMove: 'Hold your finger on the board to pour. Let go when it reaches the line.',
    },
    no: {
      instruction: 'Hold for å skjenke, slipp på streken. Over streken søler. Tre glass, stadig raskere.',
      howMove: 'Hold fingeren på brettet for å skjenke. Slipp når det når streken.',
    },
  },
});
